import { z } from 'zod';

import { makeEndpoint } from '../common';

export const IdeatedExperienceSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  headline: z.string(),
  description: z.string(),
  price: z.number(),
  vibeIds: z.array(z.string().uuid()).max(3),
  categoryId: z.string().uuid().nullable(),
  duration: z.number(),
  capacity: z.number(),
  refinementPrompt: z.string().nullable(),
  versionsCount: z.number(),
  updatedAt: z
    .date()
    .transform((date) => date.toISOString())
    .or(z.string().datetime()),
  aiCurationSessionId: z.string().uuid(),
  experienceId: z.string().uuid().nullable(),
});
export type IdeatedExperience = z.infer<typeof IdeatedExperienceSchema>;

export const IdeatedExperienceResponseSchema = IdeatedExperienceSchema;

export const IdeatedExperiencesResponseSchema = z.array(IdeatedExperienceSchema);

export const PostIdeatedExperiencesEndpoint = makeEndpoint({
  method: 'POST',
  url: ({ brandId, aiCurationSessionId }: { brandId: string; aiCurationSessionId: string }) =>
    `v1/ai/brands/${brandId}/curation-sessions/${aiCurationSessionId}/ideated-experiences`,
  requestSchema: z.void(),
  responseSchema: IdeatedExperiencesResponseSchema,
});

export const GetIdeatedExperiencesEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ brandId, aiCurationSessionId }: { brandId: string; aiCurationSessionId: string }) =>
    `v1/ai/brands/${brandId}/curation-sessions/${aiCurationSessionId}/ideated-experiences`,
  requestSchema: z.void(),
  responseSchema: IdeatedExperiencesResponseSchema,
});

export const GetIdeatedExperienceEndpoint = makeEndpoint({
  method: 'GET',
  url: ({
    brandId,
    aiCurationSessionId,
    experienceId,
  }: {
    brandId: string;
    aiCurationSessionId: string;
    experienceId: string;
  }) => `/v1/ai/brands/${brandId}/curation-sessions/${aiCurationSessionId}/ideated-experiences/${experienceId}`,
  requestSchema: z.void(),
  responseSchema: IdeatedExperienceResponseSchema,
});

export const DeleteIdeatedExperienceEndpoint = makeEndpoint({
  method: 'DELETE',
  url: ({
    brandId,
    aiCurationSessionId,
    experienceId,
  }: {
    brandId: string;
    aiCurationSessionId: string;
    experienceId: string;
  }) => `/v1/ai/brands/${brandId}/curation-sessions/${aiCurationSessionId}/ideated-experiences/${experienceId}`,
  requestSchema: z.void(),
  responseSchema: z.void(),
});

export const RestoreIdeatedExperienceEndpoint = makeEndpoint({
  method: 'POST',
  url: ({
    brandId,
    aiCurationSessionId,
    experienceId,
  }: {
    brandId: string;
    aiCurationSessionId: string;
    experienceId: string;
  }) => `/v1/ai/brands/${brandId}/curation-sessions/${aiCurationSessionId}/ideated-experiences/${experienceId}/restore`,
  requestSchema: z.void(),
  responseSchema: IdeatedExperienceSchema,
});

export const FineTuneIdeatedExperienceRequestSchema = z.object({
  refinementPrompt: z.string().trim().nonempty(),
});

export const FineTuneIdeatedExperienceEndpoint = makeEndpoint({
  method: 'PATCH',
  url: ({
    brandId,
    aiCurationSessionId,
    experienceId,
  }: {
    brandId: string;
    aiCurationSessionId: string;
    experienceId: string;
  }) => `/v1/ai/brands/${brandId}/curation-sessions/${aiCurationSessionId}/ideated-experiences/${experienceId}`,
  requestSchema: FineTuneIdeatedExperienceRequestSchema,
  responseSchema: IdeatedExperienceSchema,
});

export const IdeateExperienceDescriptionsRequestSchema = z.object({
  experiences: z.array(IdeatedExperienceSchema),
});

export const IdeateExperienceDescriptionsResponseSchema = z.object({
  data: z.array(z.string()),
});

export const IdeateNewExperienceImagesRequestSchema = z.object({
  count: z.number().positive(),
  prompt: z.string().trim().nonempty(),
});

export const IdeateNewExperienceImagesResponseSchema = z.object({
  data: z.array(z.string()),
});
